import {
  DEFAULT_POST_LOGIN_DESTINATION,
  POST_REGISTRATION_DESTINATION,
  VERIFY_PHONE_PATH,
} from './constants'

export function postLoginDestination(redirect: unknown) {
  return safeInternalRedirect(redirect) ?? DEFAULT_POST_LOGIN_DESTINATION
}

// Leaving the phone step: a return target still wins over the wall.
export function postRegistrationDestination(redirect: unknown) {
  return safeInternalRedirect(redirect) ?? POST_REGISTRATION_DESTINATION
}

// Only an explicit false means the phone step; unknown goes on as normal.
export function onboardingDestination({
  phoneConfirmed,
  redirect,
}: {
  phoneConfirmed: boolean | undefined
  redirect: unknown
}) {
  return phoneConfirmed === false
    ? withRedirect(VERIFY_PHONE_PATH, safeInternalRedirect(redirect))
    : postLoginDestination(redirect)
}

// Browsers strip tab/LF/CR before parsing, so "/<TAB>/evil.com" resolves
// off-origin; reject every C0 control and DEL, not just those three.
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL_CHARACTERS_RE = /[ -]/

const PLACEHOLDER_ORIGIN = 'https://internal.invalid'

// Takes unknown: query values and request bodies both land here unchecked.
export function safeInternalRedirect(value: unknown) {
  const target = Array.isArray(value) ? value[0] : value

  if (
    typeof target !== 'string' ||
    !target ||
    CONTROL_CHARACTERS_RE.test(target) ||
    !target.startsWith('/') ||
    target.startsWith('//') ||
    target.includes('\\')
  ) {
    return undefined
  }

  // Resolve like a browser would; anything off-origin is not internal.
  try {
    const url = new URL(target, PLACEHOLDER_ORIGIN)
    if (url.origin !== PLACEHOLDER_ORIGIN) return undefined
    // A server redirect must never hop into an API route or a data route.
    if (/^\/(api|_next)\//.test(url.pathname)) return undefined
  } catch {
    return undefined
  }

  return target
}

// Carries a return target onto the sign-in hop.
export function withRedirect(path: string, redirect: string | undefined) {
  return redirect ? `${path}?redirect=${encodeURIComponent(redirect)}` : path
}
